/**
 * Galleria "Uno sguardo al nostro negozio" (sections/store-gallery.liquid).
 * - Comparsa delle foto allo scorrimento (IntersectionObserver; disattivata con riduzione movimento).
 * - Filtri per reparto, con transizione morbida dove il browser supporta le View Transitions.
 * - Mosaico sempre pieno: se con il filtro attivo (o a una certa larghezza) restano celle vuote,
 *   le ultime foto "normali" vengono allargate su 2 colonne finché non ci sono più buchi.
 * - Visualizzazione a schermo intero (<dialog>): frecce, tastiera, swipe, contatore e barra di avanzamento.
 *   Scorre solo tra le foto visibili con il filtro attivo.
 */
class StoreGallery extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLButtonElement[]} */
  #slides = [];
  #index = 0;
  #pointerX = 0;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #lastColumns = 0;

  connectedCallback() {
    this.#setupReveal();
    const grid = this.querySelector('.store-gallery__grid');
    if (grid) {
      this.#resizeObserver = new ResizeObserver(() => {
        const columns = this.#columns;
        if (columns !== this.#lastColumns) this.#fillGaps();
      });
      this.#resizeObserver.observe(grid);
    }
    this.querySelectorAll('.store-gallery__filter').forEach((button) =>
      button.addEventListener('click', () => this.#filter(/** @type {HTMLElement} */ (button)))
    );
    this.querySelectorAll('.store-gallery__tile').forEach((tile) =>
      tile.addEventListener('click', () => this.#open(/** @type {HTMLButtonElement} */ (tile)))
    );

    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => this.#step(Number(/** @type {HTMLElement} */ (button).dataset.step)))
    );
    dialog.querySelector('.store-gallery__close')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === dialog.querySelector('.store-gallery__stage')) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.#step(1);
      if (event.key === 'ArrowLeft') this.#step(-1);
    });
    const stage = dialog.querySelector('.store-gallery__stage');
    stage?.addEventListener('pointerdown', (event) => {
      this.#pointerX = /** @type {PointerEvent} */ (event).clientX;
    });
    stage?.addEventListener('pointerup', (event) => {
      const delta = /** @type {PointerEvent} */ (event).clientX - this.#pointerX;
      if (Math.abs(delta) > 50) this.#step(delta < 0 ? 1 : -1);
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  get #grid() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.store-gallery__grid'));
  }

  get #columns() {
    const grid = this.#grid;
    return grid ? getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length : 0;
  }

  /** Celle vuote del mosaico, calcolate dalla posizione reale delle foto visibili. */
  #countGaps() {
    const grid = this.#grid;
    if (!grid) return 0;
    const style = getComputedStyle(grid);
    const columns = this.#columns;
    const gap = parseFloat(style.columnGap) || 0;
    const rowGap = parseFloat(style.rowGap) || 0;
    const rowHeight = parseFloat(style.gridAutoRows) || 1;
    const columnWidth = (grid.clientWidth - gap * (columns - 1)) / columns;
    let used = 0;
    let bottom = 0;
    const top = grid.getBoundingClientRect().top;
    for (const item of this.#items) {
      if (item.hidden) continue;
      const rect = item.getBoundingClientRect();
      const span = Math.round((rect.width + gap) / (columnWidth + gap));
      const rows = Math.round((rect.height + rowGap) / (rowHeight + rowGap));
      used += span * rows;
      bottom = Math.max(bottom, rect.bottom - top);
    }
    const totalRows = Math.round((bottom + rowGap) / (rowHeight + rowGap));
    return totalRows * columns - used;
  }

  #fillGaps() {
    this.#lastColumns = this.#columns;
    this.#items.forEach((item) => item.classList.remove('store-gallery__item--fill'));
    if (this.#lastColumns < 2) return;
    const candidates = this.#items
      .filter((item) => !item.hidden && item.classList.contains('store-gallery__item--normal'))
      .reverse();
    for (const item of candidates) {
      if (this.#countGaps() <= 0) break;
      item.classList.add('store-gallery__item--fill');
    }
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('.store-gallery__lightbox'));
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.store-gallery__item')]);
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #setupReveal() {
    if (!('IntersectionObserver' in window) || this.#reducedMotion) return;
    this.classList.add('is-ready');
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          this.#observer?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    this.#items.forEach((item) => this.#observer?.observe(item));
  }

  /** @param {HTMLElement} button */
  #filter(button) {
    const value = button.dataset.filter ?? 'all';
    this.querySelectorAll('.store-gallery__filter').forEach((other) =>
      other.setAttribute('aria-pressed', String(other === button))
    );

    const apply = () => {
      for (const item of this.#items) {
        item.hidden = value !== 'all' && item.dataset.category !== value;
        // Dopo un filtro le foto restano visibili senza ripetere la comparsa.
        if (!item.hidden) item.classList.add('is-visible');
      }
      this.#fillGaps();
    };

    if ('startViewTransition' in document && !this.#reducedMotion) {
      /** @type {any} */ (document).startViewTransition(apply);
    } else {
      apply();
    }
  }

  /** @param {HTMLButtonElement} tile */
  #open(tile) {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#slides = this.#items
      .filter((item) => !item.hidden)
      .map((item) => /** @type {HTMLButtonElement} */ (item.querySelector('.store-gallery__tile')));
    this.#index = Math.max(0, this.#slides.indexOf(tile));
    this.#render(false);
    dialog.showModal();
  }

  /** @param {number} direction */
  #step(direction) {
    if (!this.#slides.length) return;
    this.#index = (this.#index + direction + this.#slides.length) % this.#slides.length;
    this.#render(true);
  }

  /** @param {boolean} animate */
  #render(animate) {
    const dialog = this.#dialog;
    const tile = this.#slides[this.#index];
    if (!dialog || !tile) return;

    const image = /** @type {HTMLImageElement} */ (dialog.querySelector('.store-gallery__full'));
    const total = this.#slides.length;
    const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

    const swap = () => {
      image.src = tile.dataset.full ?? '';
      image.alt = tile.dataset.alt ?? '';
      const reveal = () => image.classList.remove('is-changing');
      if (image.complete) reveal();
      else image.addEventListener('load', reveal, { once: true });
    };

    if (animate && !this.#reducedMotion) {
      image.classList.add('is-changing');
      setTimeout(swap, 180);
    } else {
      swap();
    }

    const category = dialog.querySelector('.store-gallery__full-category');
    const title = dialog.querySelector('.store-gallery__full-title');
    const counter = dialog.querySelector('.store-gallery__counter');
    if (category) category.textContent = tile.dataset.category ?? '';
    if (title) title.textContent = tile.dataset.title ?? '';
    if (counter) counter.innerHTML = `<strong>${pad(this.#index + 1)}</strong> / ${pad(total)}`;
    dialog.style.setProperty('--progress', `${((this.#index + 1) / total) * 100}%`);

    // Precarica le foto vicine per uno scorrimento senza attese.
    for (const offset of [1, -1]) {
      const next = this.#slides[(this.#index + offset + total) % total];
      if (next?.dataset.full) new Image().src = next.dataset.full;
    }
  }
}

if (!customElements.get('store-gallery')) {
  customElements.define('store-gallery', StoreGallery);
}
